import React, { useState, useEffect, memo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocalStorage } from '@rehooks/local-storage';
import {
  Box,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { usePublisherCommon } from '../hooks/publisher-common.hook';
import { setPublisherName } from '../context/publisherSlice';

const PublisherSelect = memo(function PublisherSelect({
  publisherId,
  setPublisherId,
  roles,
}) {
  const {user} = useSelector((state) => state.auth);  

  const [publisherName, setPublisherNameLocal] = useLocalStorage(`sooth-publisherName`, 'The Economist');
  const [storedPublisherId, setStoredPublisherId] = useLocalStorage(`sooth-publisherId`, 1122);

  const { publisherDetails } = useSelector((state) => state.publisher);
  
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setPublisherName(publisherName));
    if (storedPublisherId !== null && storedPublisherId !== publisherId) {
      setPublisherId(storedPublisherId);
    }
  }, [publisherName, storedPublisherId, publisherId, setPublisherId, dispatch]);

  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const previousInputValue = useRef('');
  const userIsTyping = useRef(false);

  const { publisherList, setQueryString, getPublisherList } =
    usePublisherCommon();

  const initialOption = React.useMemo(() => {
    return publisherName && publisherId
      ? { sourcesId: publisherId, entityName: publisherName }
      : null;
  }, [publisherId, publisherName]);

  const options = React.useMemo(() => {
    const base = Array.isArray(publisherList) ? publisherList : [];
    const merged = !userIsTyping.current && initialOption
      ? [initialOption, ...base]
      : base;
    const seen = new Set();
    return merged.filter((opt) => {
      const key = opt?.sourcesId ?? opt?.entityName;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [publisherList, initialOption]);

  const handlePublisherSelect = (_, newValue) => {
    userIsTyping.current = false;
    setSelectedValue(newValue);
    setInputValue(newValue?.entityName ?? '');
    const newId = newValue?.sourcesId ?? null;
    setPublisherId(newId);
    setStoredPublisherId(newId);
    setPublisherNameLocal(newValue?.entityName ?? '');
    dispatch(setPublisherName(newValue?.entityName ?? ''));
  };

  const handleInputChange = (_, newInputValue) => {
    userIsTyping.current = true;
    setInputValue(newInputValue);
    if (newInputValue !== (selectedValue?.entityName || '')) {
      setSelectedValue(null);
    }
  };

  const handleNonAdminPublisherChange = (e) => {
    const selectedId = e.target.value;
    const selectedPublisher = publisherDetails.find(p => 
      p.publisherId === selectedId || p.sourcesId === selectedId
    );
    setPublisherId(selectedId);
    setStoredPublisherId(selectedId);
    const publisherNameToSet = selectedPublisher?.publisherName || selectedPublisher?.entityName || '';
    setPublisherNameLocal(publisherNameToSet);
    dispatch(setPublisherName(publisherNameToSet));
  };

  useEffect(() => {
    if (roles && roles.includes('Admin')) {
      if (!userIsTyping.current) {
        if (initialOption) {
          setSelectedValue(initialOption);
          setInputValue(initialOption.entityName);
        } else {
          setSelectedValue(null);
          setInputValue('');
        }
      }
    }
  }, [initialOption, roles]);

  useEffect(() => {
    if (roles && roles.includes('Admin')) {
      if (inputValue !== previousInputValue.current) {
        previousInputValue.current = inputValue;

        if (inputValue.length >= 2) {
          const timeoutId = setTimeout(async () => {
            setLoading(true);
            setQueryString(inputValue);
            try {
              await getPublisherList(inputValue);
            } catch (err) {
              console.error('Error fetching publishers:', err);
            } finally {
              setLoading(false);
            }
          }, 300);

          return () => clearTimeout(timeoutId);
        } else {
          setLoading(false);
          if (inputValue.length === 0) {
            setQueryString('');
          }
        }
      }
    }
  }, [inputValue, setQueryString, getPublisherList, roles]);

  if (!roles.includes('Admin')) {
    if (Array.isArray(publisherDetails) && publisherDetails.length > 1) {
      return (
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Publisher</InputLabel>
          <Select
            value={publisherId || ''}
            label="Publisher"
            onChange={handleNonAdminPublisherChange}
          >
            {publisherDetails.map((publisher) => (
              <MenuItem key={publisher.publisherId || publisher.sourcesId} value={publisher.publisherId || publisher.sourcesId}>
                {publisher.publisherName || publisher.entityName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    return (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          whiteSpace: 'nowrap',
        }}
      >
        {publisherName || 'Publisher'}
      </Typography>
    );
  }

  return (
    <Autocomplete
      sx={{ minWidth: 250 }}
      options={options}
      getOptionLabel={(option) => option?.entityName || ''}
      isOptionEqualToValue={(opt, val) => (opt?.sourcesId ?? opt?.entityName) === (val?.sourcesId ?? val?.entityName)}
      loading={loading}
      value={selectedValue}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handlePublisherSelect}
      noOptionsText={
        inputValue.length < 2
          ? 'Type at least 2 characters'
          : 'No publishers found'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={inputValue ? '' : 'Search Publisher'}
          placeholder={
            inputValue.length === 0 ? 'Type at least 2 characters' : ''
          }
          sx={{
            '& .MuiInputBase-root': { height: 56 },
            '& .MuiInputLabel-root': {
              top: '50%',
              left: 14,
              transform: 'translateY(-50%)',
            },
            '& .MuiInputLabel-shrink': {
              top: 0,
              left: 14,
              transform: 'translateY(-50%) scale(0.75)',
            },
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box
          {...props}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start !important',
            padding: '8px',
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {option.entityName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {option.corpusUrl || 'No URL available'}
          </Typography>
        </Box>
      )}
    />
  );
});

export default PublisherSelect;
